import React, {useContext, useEffect, useRef, useState} from "react";
import SearchTool from "./SearchTool";
import {connect} from "react-redux";
import {updateFocus, updateModeData, updateSearchText, updateWriterMode} from "../../../redux/slices/editor";
import {CircularProgress, IconButton} from "@mui/material";
import {CheckCircle, CheckCircleOutline, LocalLibrary, LocalLibraryOutlined, Spellcheck, Stop}
    from "@mui/icons-material";
import {selectDictionary, selectNetwork} from "../../../redux/selectors/firebase";
import {State} from "../../../fstn/state";
import {getFunctions, httpsCallable} from "firebase/functions";
import {IGrammarRule} from "../../writer/EditState";
import {FirebaseContext} from "../../firebase/FirebaseProvider";
import {selectModeData, selectWriterMode} from "../../../redux/selectors/editor";

interface IProps {
    dictionary: {[word: string]: any}
    modeData: {[key: string]: any}
    network: {states: { [key: string]: State }}
    project: string
    updateFocus: (focus: string) => void
    updateModeData: (update: {[key: string]: any}) => void
    updateSearchText: (text: string) => void
    updateWriterMode: (text: "network" | "grammar") => void
    writerMode: string
}
interface IGrammarResult {
    matches: IGrammarRule[]
}
type PendingScripts = {[state: string]: string};
function WriterTools({dictionary, modeData, network, project, updateFocus, updateModeData, updateSearchText,
                         updateWriterMode, writerMode}: IProps) {
    const [checking, setChecking] = useState(false);
    const [currentResult, setCurrentResult] = useState<string>("");
    const [searchResults, setSearchResults] = useState<string[]>([]);
    const [pendingScript, setPendingScript] = useState<number>(0);
    const [pendingScripts, setPendingScripts] = useState<PendingScripts[]>([]);
    const {remove, setGrammar} = useContext(FirebaseContext);
    const checkSentenceRef = useRef(httpsCallable<{text: string}, IGrammarResult>(
        getFunctions(undefined, "europe-west2"), "checkSentence"));
    const handleSearchText = (text: string) => {
        const searchStates = Object.keys(network.states).filter(key =>
            network.states[key].script && network.states[key].script.indexOf(text) !== -1);

        updateSearchText(text);
        setSearchResults(searchStates);
        if (searchStates.length) {
            updateFocus(searchStates[0]);
            setCurrentResult(searchStates[0]);
        }
    };
    const handleSearchUp = () => {
        if (searchResults.length) {
            const current = searchResults.indexOf(currentResult);

            if (current > 0) {
                updateFocus(searchResults[current - 1]);
                setCurrentResult(searchResults[current - 1]);
            }
        }
    };
    const handleSearchDown = () => {
        const current = searchResults.indexOf(currentResult);

        if (current < searchResults.length - 1) {
            updateFocus(searchResults[current + 1]);
            setCurrentResult(searchResults[current + 1]);
        }
    };
    const handleSpellCheckClick = () => setChecking(! checking);
    const handleGrammarModeClick = () => {
        if (writerMode === "grammar") {
            updateWriterMode("network");
        } else {
            updateWriterMode("grammar");
        }
    };
    const handleToggleShowDictionary = () => {updateModeData({showDictionary: ! modeData.showDictionary})}

    useEffect(() => {
        if (checking) {
            remove(`grammar/${project}`).then(() => remove(`ignoredGrammar/${project}`)).then(() =>{
                const pendingScripts: PendingScripts[] = [];
                const maxRequestLength = 5000;
                let scriptLength = 0;
                let scripts: PendingScripts = {};

                Object.keys(network.states).forEach(state => {
                    const text = network.states[state].script;

                    if (checking && text) {
                        if (scriptLength + text.length > maxRequestLength) {
                            pendingScripts.push(scripts)
                            scriptLength = 0;
                            scripts = {};
                        }
                        scriptLength += text.length + 1;
                        scripts[state] = text;
                    }
                });
                pendingScripts.push(scripts);
                setPendingScripts(pendingScripts);
            });
        } else {
            setPendingScript(0);
            setPendingScripts([]);
        }
    }, [checking, network, project, remove, setPendingScripts]);
    useEffect(() => {
        const dictionaryWords = Object.keys(dictionary || {}).map(word => word.toLowerCase());
        const updateGrammarRules = async (scripts: PendingScripts) => {
            const text = Object.values(scripts).join(" ");
            const result = await checkSentenceRef.current({text});
            const states = Object.keys(scripts);
            let stateIndex = 0;
            let consumed = 0;
            let grammar: {matches: unknown[], text: string} | undefined;

            for (const match of result.data.matches.filter(match => {
                if (match.shortMessage === "Spelling mistake") {
                    const word = text.substr(match.offset, match.length).trim().toLowerCase();

                    return dictionaryWords.indexOf(word) === -1;
                }
                return true;
            })) {
                let state = states[stateIndex];
                let sentence = scripts[state];

                while (match.offset > consumed + sentence.length) {
                    // Rule is out of state scope, move onto next state
                    if (grammar) {
                        // Grammar rule outstanding from previous iteration
                        await setGrammar(project, state, grammar);
                        grammar = undefined;
                    }
                    consumed += sentence.length + 1;
                    state = states[++stateIndex];
                    sentence = scripts[state];
                    if (sentence === undefined) return;
                }
                grammar = grammar || {
                    matches: [],
                    text: sentence
                }
                grammar.matches.push({
                    ...match,
                    offset: match.offset - consumed,
                    sentence
                });
            }
        };
        if (pendingScripts.length) {
            if (pendingScript < pendingScripts.length) {
                updateGrammarRules(pendingScripts[pendingScript]).then(() => setPendingScript(pendingScript + 1));
            } else {
                setPendingScripts([]);
                setPendingScript(0);
            }
        } else {
            setChecking(false);
        }
    }, [dictionary, pendingScripts, pendingScript, project, setGrammar, setPendingScript]);
    return <>
        <SearchTool
            count={searchResults.length} index={searchResults.indexOf(currentResult)}
            searchText={handleSearchText} searchUp={handleSearchUp} searchDown={handleSearchDown}/>
        <IconButton
            color="inherit"
            onClick={handleSpellCheckClick}
            title={"Spell check document"}
            size="large">
            {checking ?
                <>
                    <Stop/>
                    <CircularProgress color="inherit" size={24} variant="determinate" value={
                        Math.floor(100 * pendingScript / pendingScripts.length)
                    } style={{position: "absolute", zIndex: 1}}/>
                </> :
                <Spellcheck/>
            }
        </IconButton>
        <IconButton
            color="inherit"
            onClick={handleGrammarModeClick}
            title=
                {`Toggle grammar check mode (Currently: ${writerMode === "grammar" ? "on" : "off"})`}
            size="large">
            {writerMode === "grammar" ? <CheckCircle/> : <CheckCircleOutline/>}
        </IconButton>
        <IconButton color={"inherit"} title="Show dictionary" onClick={handleToggleShowDictionary}>
            {modeData.showDictionary ? <LocalLibrary/> : <LocalLibraryOutlined/>}
        </IconButton>
    </>;
}
const mapStateToProps = (store: never) => ({
    dictionary: selectDictionary(store),
    modeData: selectModeData(store),
    network: selectNetwork(store),
    writerMode: selectWriterMode(store)
});

export default connect(mapStateToProps, {
    updateSearchText,
    updateModeData,
    updateFocus,
    updateWriterMode
})(WriterTools);
